import { useMemo } from "react";
import { useTranslation } from "@/packages/utils";
import { FilterChips } from "../FilterChips/FilterChips";
import { ACTIVITY_CATEGORY_CONFIG } from "./types";
import type { ActivityEvent, ActivityEventCategory } from "./types";
import type { FilterChipData } from "../FilterChips/types";

// ─── Props ──────────────────────────────────────────────────────────────────

interface ActivityFiltersProps {
  items: ActivityEvent[];
  activeFilters: string[];
  onFilterChange: (filter: string) => void;
  isMultiSelect?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ActivityFilters({
  items,
  activeFilters,
  onFilterChange,
  isMultiSelect = false,
}: ActivityFiltersProps) {
  const { t } = useTranslation(["common"]);
  const chips = useMemo<FilterChipData[]>(() => {
    const counts: Record<string, number> = { all: items.length };
    for (const item of items) {
      counts[item.category] = (counts[item.category] ?? 0) + 1;
    }

    const result: FilterChipData[] = [
      { id: "all", label: t("common:labels.all"), count: counts.all },
    ];

    const categories: ActivityEventCategory[] = [
      "status",
      "content",
      "people",
      "system",
    ];
    for (const cat of categories) {
      const count = counts[cat] ?? 0;
      if (count > 0) {
        result.push({
          id: cat,
          label: ACTIVITY_CATEGORY_CONFIG[cat].label,
          count,
        });
      }
    }

    return result;
  }, [items, t]);

  // Determine active state based on multi-select or single-select mode
  const activeFilter = isMultiSelect
    ? activeFilters.includes("all")
      ? "all"
      : activeFilters[0] || "all"
    : activeFilters[0] || "all";

  return (
    <FilterChips
      chips={chips}
      activeFilter={activeFilter}
      onFilterChange={onFilterChange}
      layoutId="activeFilterBg"
      multiSelect={isMultiSelect}
      selectedIds={isMultiSelect ? activeFilters : undefined}
    />
  );
}
